import type { FormikProps } from 'formik'

import { BodyFlow, H1Flow, MaterialIcons } from '@monthio/react-components'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { BudgetHelperQuestionnaireInputs } from '../../../types'

interface StepConfig {
	description?: string
	invalidReason?: string
	children: (clickHandler: {
		setIsInvalid: (event: any) => void
	}) => React.ReactElement
}

export interface StepProps {
	handleClick: (value: string) => void
	formProps: FormikProps<BudgetHelperQuestionnaireInputs>
}

const DescriptionWrapper = styled.div`
	display: flex;
	background: #def1fc;
	gap: 8px;
	margin-top: 8px;
	padding: 8px;
	color: #198ccd;
	border-radius: 4px;
	font-size: 14px;
`

const StepWrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	${(props) => props.theme.breakpoints.up('sm')} {
		justify-content: normal;
		gap: 32px;
	}
`

const InvalidBannerWrapper = styled.div`
	border-radius: 12px 12px 0 0;
	border-top: 1px solid #e7e8ee;
	box-shadow: 0px -4px 24px 0px rgba(53, 64, 82, 0.08);
	background: #fff;
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 16px 25px 25px;
	display: flex;
	flex-direction: column;
	gap: 8px;

	${(props) => props.theme.breakpoints.up('sm')} {
		position: relative;
		bottom: -26px;
	}
`

const StyledH1 = styled(H1Flow)`
	margin: 0;
`

const SadFace = styled.div`
	font-size: 40px;
`

const QuestionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
`

const Description: React.FC<{ description: string }> = ({ description }) => {
	return (
		<DescriptionWrapper>
			<MaterialIcons.InfoOutlined fontSize="small" />
			<div>{description}</div>
		</DescriptionWrapper>
	)
}

const InvalidBanner: React.FC<{ reason: string }> = ({ reason }) => {
	const { t } = useTranslation()
	return (
		<InvalidBannerWrapper>
			<SadFace role="img" aria-label="sad face">
				🙁
			</SadFace>
			<StyledH1>{t('budgetHelper-invalidBanner-title')}</StyledH1>
			<BodyFlow>{reason}</BodyFlow>
		</InvalidBannerWrapper>
	)
}

export const Step: React.FC<StepConfig> = ({
	description,
	invalidReason,
	children,
}) => {
	const { t } = useTranslation()

	const [isInvalid, setIsInvalid] = useState(false)
	return (
		<StepWrapper>
			<QuestionWrapper>{children({ setIsInvalid })}</QuestionWrapper>
			{isInvalid && invalidReason ? (
				<InvalidBanner reason={t(invalidReason)} />
			) : description ? (
				<Description description={t(description)} />
			) : null}
		</StepWrapper>
	)
}
